/**
 * Retirement adequacy — the arithmetic behind "What It Would Take".
 *
 * The engine answers one question about the drawdown: how long does the corpus
 * you have actually last. It returns `swp.lastsYears` and stops there. The
 * section that makes that number actionable asks the two inverse questions —
 * what corpus would sustain the income you asked for, and what income the
 * corpus you have can actually sustain — and neither is in the response.
 *
 * WHY THE FORMULA LOOKS LIKE THIS
 *
 * The withdrawal is paid monthly but steps up once a year with inflation, so it
 * is not a single growing annuity. It is a series of twelve-month ordinary
 * annuities, each 8% larger than the last, discounted back at the monthly rate:
 *
 *     PV = Σ  w·(1+g)^k · a₁₂ / (1+r)^(12k)      for k = 0 … n−1
 *     a₁₂ = (1 − (1+r)^−12) / r
 *     r   = annualReturn / 12          (simple, not compounded)
 *
 * The simple monthly rate is not an approximation — it is the convention the
 * planning engine itself uses, confirmed by reproducing `swp.lastsYears` of
 * 3.1667 exactly from a ₹70,24,115 corpus at ₹2,00,000 a month.
 *
 * This reproduces the sample report to four decimal places: ₹4.0649 cr /
 * ₹4.9297 cr / ₹5.7413 cr for 20, 25 and 30 years, against its printed ₹4.06 cr
 * / ₹4.93 cr / ₹5.74 cr. Discrete simulation does NOT reproduce those figures —
 * withdrawing at period end lands 0.3% high and at period start 0.3% low, which
 * is how the closed form was identified.
 */

import type { PlannerSwpResult } from "./types";

/** Horizons the section sizes, in years. */
export const ADEQUACY_HORIZONS = [20, 25, 30] as const;

/**
 * Present value of a monthly withdrawal that steps up annually with inflation.
 *
 * Returns the corpus needed on day one to pay `monthly` for `years`, ending at
 * exactly zero. Guards the degenerate inputs rather than returning Infinity:
 * a zero or negative return has no discounting to do.
 */
export const corpusForYears = (
  years: number,
  monthly: number,
  returnPct: number,
  inflationPct: number
): number => {
  if (!(years > 0) || !(monthly > 0)) return 0;
  const r = returnPct / 1200;
  const g = inflationPct / 100;
  // No return at all: the corpus is simply every payment, unmediated.
  if (r <= 0) {
    let total = 0;
    for (let k = 0; k < years; k += 1) total += monthly * 12 * Math.pow(1 + g, k);
    return total;
  }
  const annuity = (1 - Math.pow(1 + r, -12)) / r;
  let pv = 0;
  for (let k = 0; k < years; k += 1) {
    pv += (monthly * Math.pow(1 + g, k) * annuity) / Math.pow(1 + r, 12 * k);
  }
  return pv;
};

/**
 * The inverse: what a given corpus can pay for a given number of years.
 *
 * Present value is linear in the payment, so this is a ratio rather than a
 * second search — which is also why the sample report's sustainable draws are
 * exactly its corpus figures scaled, and why the two tables in the section can
 * never disagree with each other.
 */
export const sustainableDraw = (
  corpus: number,
  years: number,
  returnPct: number,
  inflationPct: number
): number => {
  const reference = 100000;
  const needed = corpusForYears(years, reference, returnPct, inflationPct);
  if (!(needed > 0)) return 0;
  return (reference * corpus) / needed;
};

export interface AdequacyRow {
  years: number;
  /** "25 years, to age 69" — the age is what makes the row mean something. */
  toAge: number;
  /** Corpus that would sustain the planned withdrawal for this long. */
  corpusNeeded: number;
  /** What the corpus actually built can pay, for this long. */
  draw: number;
  /** That draw as a share of what was planned. */
  drawSharePct: number;
}

export interface RetirementAdequacy {
  /** What the plan actually builds — `swp.startCorpus`. */
  built: number;
  /** The withdrawal that was asked for — `swp.net`. */
  planned: number;
  /** True when the plan already sustains the ask; the section is then dropped. */
  sufficient: boolean;
  /** Age at the drawdown start date. */
  startAge: number;
  /** How long the built corpus lasts, in years — `swp.lastsYears`. */
  lastsYears: number;
  /** Age when it runs out. */
  exhaustedAge: number;
  /** Planned withdrawal as a share of the corpus, annualised. */
  withdrawalRatePct: number;
  /** (1+return)/(1+inflation) − 1. The number that decides whether it can work. */
  realReturnPct: number;
  rows: AdequacyRow[];
  /** The horizon a reader anchors on, and the one the copy quotes. */
  headline: AdequacyRow;
  /** `headline.corpusNeeded` ÷ what the plan builds. "seven times". */
  headlineMultiple: number;
  /**
   * `headline.corpusNeeded` discounted to today at goal inflation — the honest
   * size of the target, in the money the client actually thinks in.
   */
  headlineInTodaysMoney: number;
}

/**
 * `yearsToStart` and `startAge` come from the goal timeline, not from the SWP
 * block, which carries no age. `inflationForToday` is the GOAL's inflation —
 * discounting a retirement target by the drawdown's inflation would be mixing
 * two different assumptions to answer one question.
 */
export const buildRetirementAdequacy = (
  swp: PlannerSwpResult,
  options: {
    startAge: number;
    yearsToStart: number;
    inflationForToday: number;
    headlineYears?: number;
  }
): RetirementAdequacy | null => {
  const built = swp.startCorpus;
  const planned = swp.net;
  if (!(planned > 0) || !(built > 0)) return null;

  const rows: AdequacyRow[] = ADEQUACY_HORIZONS.map((years) => {
    const corpusNeeded = corpusForYears(years, planned, swp.ret, swp.infl);
    const draw = sustainableDraw(built, years, swp.ret, swp.infl);
    return {
      years,
      // Whole years, floored: someone who retires at 44 years 7 months is 64
      // after twenty years of drawdown, not 65.
      toAge: Math.floor(options.startAge) + years,
      corpusNeeded,
      draw,
      drawSharePct: (draw / planned) * 100,
    };
  });

  const headlineYears = options.headlineYears ?? 25;
  const headline =
    rows.find((row) => row.years === headlineYears) ?? rows[rows.length - 1];

  return {
    built,
    planned,
    sufficient: swp.everlasting || !swp.exhausted,
    startAge: options.startAge,
    lastsYears: swp.lastsYears,
    exhaustedAge: Math.floor(options.startAge + swp.lastsYears),
    withdrawalRatePct: ((planned * 12) / built) * 100,
    realReturnPct: ((1 + swp.ret / 100) / (1 + swp.infl / 100) - 1) * 100,
    rows,
    headline,
    headlineMultiple: headline.corpusNeeded / built,
    headlineInTodaysMoney:
      headline.corpusNeeded /
      Math.pow(1 + options.inflationForToday / 100, options.yearsToStart),
  };
};
